import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { ContractFieldChangeResponse } from '@interfaces/contracts/responses/contract-field-change.response';
import { PartnerContractRevisionListItemResponse } from '@interfaces/partners/contracts/responses/partner-contract-revision.response';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import { SharedModule } from '@src/app/shared/shared.module';
import {
  formatContractPercent,
  partnerContractRevisionActionLabel,
} from '../../../../utils/partner-contract.utils';

/**
 * Histórico de revisões da minuta (card 3462, MG4), no painel lateral do editor. Mesmo desenho do
 * histórico de versões do modelo (`styles/_history-panel.scss`). Clicar numa revisão abre a
 * pré-visualização daquele texto.
 *
 * Abaixo das revisões, as **alterações de valores** (card 3643): quem mudou um percentual, quando,
 * de quanto para quanto e o padrão do campo.
 *
 * Reaproveitado pelo editor dos contratos de cliente (card 3643), que passa o próprio formatador
 * (R$ ou %, pelo catálogo), os rótulos das ações e os textos de lista vazia. Sem esses inputs, o
 * comportamento é o do contrato de parceria.
 */
@Component({
  selector: 'app-partner-contract-revision-history',
  standalone: true,
  imports: [CommonModule, SharedModule, VaultDateTimePipe],
  templateUrl: './partner-contract-revision-history.component.html',
  styleUrls: ['./partner-contract-revision-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerContractRevisionHistoryComponent {
  readonly revisions = input<PartnerContractRevisionListItemResponse[]>([]);
  /** Alterações de valores, mais recente primeiro (`GET .../{id}/field-changes`). */
  readonly fieldChanges = input<ContractFieldChangeResponse[]>([]);
  readonly loading = input(false);
  /** Revisão aberta na pré-visualização, para destacar na lista. */
  readonly selectedId = input<string | null>(null);

  readonly selectRevision = output<PartnerContractRevisionListItemResponse>();
  readonly collapse = output<void>();

  /** Formata o valor de uma alteração (padrão: percentual, como no contrato de parceria). */
  readonly valueFormatter = input<
    (value: number | string | null | undefined, fieldKey?: string) => string
  >(value => formatContractPercent(value));
  /** Rótulo da ação da revisão em português. */
  readonly actionLabeler = input<(action: string | null | undefined) => string>(
    partnerContractRevisionActionLabel
  );
  readonly emptyRevisionsText = input(
    'Ainda não há revisões registradas. Elas nascem ao criar, editar o texto ou o título, preencher variáveis, finalizar e reabrir.'
  );
  readonly emptyFieldChangesText = input(
    'Nenhum percentual foi alterado desde que o contrato foi gerado.'
  );

  actionLabel(action: string | null | undefined): string {
    return this.actionLabeler()(action);
  }

  formatValue(value: number | string | null | undefined, fieldKey?: string): string {
    return this.valueFormatter()(value, fieldKey);
  }
}
